import {
  AutocompleteInputComponent,
  AutocompleteItem,
} from '@delmonicos/shared-design-system-react/src/components/autocomplete-input/autocomplete-input.component';
import { useSetOrRemoveChargePointsLocationFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/inputs/set-or-remove-charge-points-location.form-validation';
import { SetOrRemoveChargePointsLocationModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/inputs/set-or-remove-charge-points-location.model';
import { useEffect, useState } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  useCpmsBoGetLocationQuery,
  useCpmsBoGetLocationsPaginatedQuery,
} from '@app/core/service/cpms-bo-location.service';

import { useChargePointContext } from '../../charge-point.context';

const ChargePointLocationUpdateFormStep1LocationSelection = ({
  field,
  fieldState: { error },
}: {
  field: ControllerRenderProps<
    SetOrRemoveChargePointsLocationModel,
    'locationId'
  >;
  fieldState: ControllerFieldState;
}) => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { locationId } = useSetOrRemoveChargePointsLocationFormValidation();
  const {
    customer: { id: customerId },
    network: { id: networkId },
  } = useChargePointContext();

  // SEARCH TERM ///////////////////////////////////////////////////////////////

  const [search, setSearch] = useState<string>('');

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const {
    data: locationsPaginatedQueryData,
    isSuccess: locationsPaginatedQueryIsSuccess,
  } = useCpmsBoGetLocationsPaginatedQuery(
    {
      limit: 10,
      offset: 0,
      customerId,
      networkId,
      name: search === '' ? undefined : `~${search}`,
    },
    {
      skip: !customerId,
    },
  );

  const { data: locationQueryData, isSuccess: locationQueryIsSuccess } =
    useCpmsBoGetLocationQuery(field.value!, {
      skip: !field.value,
    });

  // AUTOCOMPLETE ITEM /////////////////////////////////////////////////////////

  const [selectedItem, setSelectedItem] = useState<AutocompleteItem | null>(
    null,
  );

  useEffect(() => {
    if (locationQueryIsSuccess) {
      const location =
        locationQueryData.id === field.value ? locationQueryData : null;

      if (location) {
        setSelectedItem({
          key: locationQueryData.id,
          label: locationQueryData.name,
        });
        return;
      }
    }

    setSelectedItem(null);
  }, [locationQueryData, locationQueryIsSuccess, field.value]);

  // AUTOCOMPLETE ITEMS ////////////////////////////////////////////////////////

  const [autocompleteItems, setAutocompleteItems] = useState<
    AutocompleteItem[]
  >([]);

  useEffect(() => {
    if (locationsPaginatedQueryIsSuccess) {
      setAutocompleteItems(
        locationsPaginatedQueryData.docs.map(({ id, name }) => ({
          key: id,
          label: name,
        })) ?? [],
      );
    }
  }, [search, locationsPaginatedQueryData, locationsPaginatedQueryIsSuccess]);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <AutocompleteInputComponent
      label={t('delmonicos-shared-sdk.outputs-gateway-charge-point.location')}
      placeholder={t(
        'delmonicos-shared-sdk.outputs-gateway-charge-point.location',
      )}
      required={!!locationId.required}
      value={search}
      onChange={setSearch}
      onSearch={setSearch}
      items={autocompleteItems}
      selectedItem={selectedItem}
      onSelect={(selectedItemNext) => {
        field.onChange(selectedItemNext?.key ?? '');
      }}
      emptyResultText={t('global.no-result-found')}
      errorMessage={error?.message}
    />
  );
};

export const ChargePointLocationUpdateFormStep1Controller =
  (): React.JSX.Element => {
    // CONTEXT /////////////////////////////////////////////////////////////////

    const { control } = useFormContext<SetOrRemoveChargePointsLocationModel>();
    const { locationId } = useSetOrRemoveChargePointsLocationFormValidation();

    // RENDER ////////////////////////////////////////////////////////////////////

    return (
      <Controller
        name="locationId"
        rules={locationId}
        control={control}
        render={ChargePointLocationUpdateFormStep1LocationSelection}
      />
    );
  };
